import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import './PropostasCadastro.css'

const ORIGENS = ['PAP', 'INDICAÇÃO', 'LISTA BANCO', 'LIGAÇÃO']

const getInicioFimMes = (mesAno) => {
  if (!mesAno || mesAno.length < 7) {
    const d = new Date()
    mesAno = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }
  const [ano, mes] = mesAno.split('-').map(Number)
  const inicio = new Date(ano, mes - 1, 1)
  const fim = new Date(ano, mes, 0)
  return {
    inicio: inicio.toISOString().split('T')[0],
    fim: fim.toISOString().split('T')[0]
  }
}

const formatarMoeda = (v) =>
  Number(v) != null && !isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0,00'

// Formata o valor digitado no campo de valor da proposta (R$) como moeda brasileira enquanto o usuário digita
const formatarValorInput = (valor) => {
  if (valor == null) return ''
  let digits = String(valor).replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 1) {
    digits = `0${digits}`
  }
  const inteiro = digits.slice(0, -2)
  const centavos = digits.slice(-2)
  const inteiroComPontos = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `${inteiroComPontos},${centavos}`
}

const formatarData = (dataStr) => {
  if (!dataStr) return '–'
  const s = String(dataStr).split('T')[0]
  const [y, m, d] = s.split('-')
  return d && m && y ? `${d}/${m}/${y}` : s
}

const formatarDataHora = (dataHoraStr) => {
  if (!dataHoraStr) return '–'
  const d = new Date(dataHoraStr)
  if (isNaN(d.getTime())) return String(dataHoraStr)
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dia}/${mes}/${ano} ${h}:${min}`
}

const PropostasCadastro = ({ onBack, userRole, userEmpresa }) => {
  const hoje = new Date()
  const mesAnoPadrao = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
  const [showFiltros, setShowFiltros] = useState(false)
  const [mesAno, setMesAno] = useState(mesAnoPadrao)
  const [filtroVendedor, setFiltroVendedor] = useState('')
  const [propostas, setPropostas] = useState([])
  const [vendedores, setVendedores] = useState([])
  const [eventosCalendario, setEventosCalendario] = useState([])
  const [eventosDiaHoje, setEventosDiaHoje] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingEventos, setLoadingEventos] = useState(true)
  const [loadingEventosDia, setLoadingEventosDia] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showModalVendedor, setShowModalVendedor] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingVendedor, setSavingVendedor] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [editingPropostaId, setEditingPropostaId] = useState(null)
  const [editingVendedorId, setEditingVendedorId] = useState(null)
  const [formVendedor, setFormVendedor] = useState({ nome: '', meta: '' })
  const [formData, setFormData] = useState({
    nome_cliente: '',
    id_vendedor: '',
    data: hoje.toISOString().split('T')[0],
    valor_proposta: '',
    origem: 'PAP',
    virou: false
  })

  const loadVendedores = useCallback(async () => {
    try {
      let query = supabase
        .from('vendedores_proposta')
        .select('id, nome, meta')
        .order('nome')
      if (userEmpresa?.id) {
        query = query.or(`id_empresa.eq.${userEmpresa.id},id_empresa.is.null`)
      }
      const { data, error } = await query
      if (error) throw error
      setVendedores(data || [])
    } catch (err) {
      console.error('Erro ao carregar vendedores:', err)
    }
  }, [userEmpresa?.id])

  const loadPropostas = useCallback(async () => {
    try {
      setLoading(true)
      const { inicio, fim } = getInicioFimMes(mesAno)
      let query = supabase
        .from('propostas_cadastro')
        .select(`
          id,
          nome_cliente,
          id_vendedor,
          data,
          valor_proposta,
          origem,
          virou,
          vendedores_proposta(id, nome, meta)
        `)
        .gte('data', inicio)
        .lte('data', fim)
        .order('data', { ascending: false })
      if (userEmpresa?.id) {
        query = query.or(`id_empresa.eq.${userEmpresa.id},id_empresa.is.null`)
      }
      if (filtroVendedor !== '') {
        const idV = parseInt(filtroVendedor, 10)
        if (!isNaN(idV)) query = query.eq('id_vendedor', idV)
      }
      const { data, error } = await query
      if (error) throw error
      setPropostas(data || [])
    } catch (err) {
      console.error('Erro ao carregar propostas:', err)
      setPropostas([])
    } finally {
      setLoading(false)
    }
  }, [mesAno, filtroVendedor, userEmpresa?.id])

  const loadEventosCalendario = useCallback(async () => {
    try {
      setLoadingEventos(true)
      const { inicio, fim } = getInicioFimMes(mesAno)
      const inicioTs = `${inicio}T00:00:00`
      const fimTs = `${fim}T23:59:59`
      let query = supabase
        .from('eventos_calendario')
        .select('id, data_hora, id_responsavel')
        .gte('data_hora', inicioTs)
        .lte('data_hora', fimTs)
        .is('id_ordem_servico', null)
      if (userEmpresa?.id) {
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', userEmpresa.id)
        const ids = (usuariosEmpresa || []).map((u) => u.id)
        if (ids.length > 0) {
          query = query.in('id_responsavel', ids)
        } else {
          setEventosCalendario([])
          return
        }
      }
      const { data, error } = await query
      if (error) throw error
      const eventos = data || []
      const idsResponsavel = [...new Set(eventos.map((e) => e.id_responsavel).filter(Boolean))]
      let mapNomes = {}
      if (idsResponsavel.length > 0) {
        const { data: usuarios } = await supabase
          .from('usuarios_sistema')
          .select('id, nome')
          .in('id', idsResponsavel)
        mapNomes = (usuarios || []).reduce((acc, u) => {
          acc[u.id] = u.nome || 'Responsável'
          return acc
        }, {})
      }
      setEventosCalendario(
        eventos.map((e) => ({
          ...e,
          nome_responsavel: mapNomes[e.id_responsavel] || 'Responsável'
        }))
      )
    } catch (err) {
      console.error('Erro ao carregar eventos do calendário:', err)
      setEventosCalendario([])
    } finally {
      setLoadingEventos(false)
    }
  }, [mesAno, userEmpresa?.id])

  const loadEventosDiaHoje = useCallback(async () => {
    try {
      setLoadingEventosDia(true)
      const d = new Date()
      const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      const fim = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
      const inicioTs = inicio.toISOString()
      const fimTs = fim.toISOString()
      let query = supabase
        .from('eventos_calendario')
        .select('id, nome_evento, data_hora, id_responsavel')
        .gte('data_hora', inicioTs)
        .lte('data_hora', fimTs)
        .is('id_ordem_servico', null)
        .order('data_hora', { ascending: true })
      if (userEmpresa?.id) {
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', userEmpresa.id)
        const ids = (usuariosEmpresa || []).map((u) => u.id)
        if (ids.length > 0) {
          query = query.in('id_responsavel', ids)
        } else {
          setEventosDiaHoje([])
          return
        }
      }
      const { data, error } = await query
      if (error) throw error
      const eventos = data || []
      const idsResponsavel = [...new Set(eventos.map((e) => e.id_responsavel).filter(Boolean))]
      let mapNomes = {}
      if (idsResponsavel.length > 0) {
        const { data: usuarios } = await supabase
          .from('usuarios_sistema')
          .select('id, nome')
          .in('id', idsResponsavel)
        mapNomes = (usuarios || []).reduce((acc, u) => {
          acc[u.id] = u.nome || 'Responsável'
          return acc
        }, {})
      }
      setEventosDiaHoje(
        eventos.map((e) => ({
          ...e,
          nome_responsavel: mapNomes[e.id_responsavel] || 'Responsável'
        }))
      )
    } catch (err) {
      console.error('Erro ao carregar agendamentos do dia:', err)
      setEventosDiaHoje([])
    } finally {
      setLoadingEventosDia(false)
    }
  }, [userEmpresa?.id])

  useEffect(() => {
    loadVendedores()
  }, [loadVendedores])
  useEffect(() => {
    loadPropostas()
  }, [loadPropostas])
  useEffect(() => {
    loadEventosCalendario()
  }, [loadEventosCalendario])
  useEffect(() => {
    loadEventosDiaHoje()
  }, [loadEventosDiaHoje])

  const dadosGraficoVendedor = useMemo(() => {
    const porVendedor = {}
    propostas.forEach((p) => {
      const id = p.id_vendedor
      const nome = p.vendedores_proposta?.nome ?? `Vendedor ${id}`
      if (!porVendedor[id]) {
        porVendedor[id] = { nome, valor: 0, qtd: 0 }
      }
      porVendedor[id].valor += Number(p.valor_proposta) || 0
      porVendedor[id].qtd += 1
    })
    return Object.entries(porVendedor)
      .map(([id, o]) => ({ id, nome: o.nome, valor: o.valor, qtd: o.qtd }))
      .sort((a, b) => b.valor - a.valor)
  }, [propostas])

  const dadosGraficoOrigem = useMemo(() => {
    const porOrigem = {}
    propostas.forEach((p) => {
      const o = p.origem || 'Outro'
      if (!porOrigem[o]) porOrigem[o] = { valor: 0, qtd: 0 }
      porOrigem[o].valor += Number(p.valor_proposta) || 0
      porOrigem[o].qtd += 1
    })
    return ORIGENS.map((origem) => ({
      origem,
      valor: porOrigem[origem]?.valor ?? 0,
      qtd: porOrigem[origem]?.qtd ?? 0
    })).filter((d) => d.qtd > 0 || d.valor > 0)
  }, [propostas])

  const totalValor = useMemo(
    () => propostas.reduce((s, p) => s + (Number(p.valor_proposta) || 0), 0),
    [propostas]
  )

  const totalValorVirado = useMemo(
    () => propostas
      .filter((p) => p.virou === true)
      .reduce((s, p) => s + (Number(p.valor_proposta) || 0), 0),
    [propostas]
  )

  const qtdPAP = useMemo(
    () => propostas.filter((p) => p.origem === 'PAP').length,
    [propostas]
  )
  const qtdListaBanco = useMemo(
    () => propostas.filter((p) => p.origem === 'LISTA BANCO').length,
    [propostas]
  )
  const qtdIndicacao = useMemo(
    () => propostas.filter((p) => p.origem === 'INDICAÇÃO').length,
    [propostas]
  )
  const qtdLigacao = useMemo(
    () => propostas.filter((p) => p.origem === 'LIGAÇÃO').length,
    [propostas]
  )
  const qtdAgendamento = useMemo(() => eventosCalendario.length, [eventosCalendario])

  const maxValorVendedor = useMemo(
    () => Math.max(1, ...dadosGraficoVendedor.map((d) => d.valor)),
    [dadosGraficoVendedor]
  )
  const maxValorOrigem = useMemo(
    () => Math.max(1, ...dadosGraficoOrigem.map((d) => d.valor)),
    [dadosGraficoOrigem]
  )

  const dadosGraficoAgendamentos = useMemo(() => {
    const porResponsavel = {}
    eventosCalendario.forEach((ev) => {
      const id = ev.id_responsavel
      const nome = ev.nome_responsavel || `Responsável ${id || '?'}`
      if (!porResponsavel[id]) porResponsavel[id] = { nome, count: 0 }
      porResponsavel[id].count += 1
    })
    return Object.entries(porResponsavel)
      .map(([id, o]) => ({ id, nome: o.nome, count: o.count }))
      .sort((a, b) => b.count - a.count)
  }, [eventosCalendario])

  const maxAgendamentos = useMemo(
    () => Math.max(1, ...dadosGraficoAgendamentos.map((d) => d.count)),
    [dadosGraficoAgendamentos]
  )

  const coresBarras = ['#1565c0', '#7b1fa2', '#e65100', '#2e7d32', '#c2185b', '#5d4037']

  const openModalProposta = (proposta = null) => {
    if (proposta) {
      setEditingPropostaId(proposta.id)
      setFormData({
        nome_cliente: proposta.nome_cliente || '',
        id_vendedor: String(proposta.id_vendedor || ''),
        data: proposta.data ? String(proposta.data).split('T')[0] : new Date().toISOString().split('T')[0],
        valor_proposta: proposta.valor_proposta != null ? formatarValorInput(proposta.valor_proposta) : '',
        origem: proposta.origem || 'PAP',
        virou: !!proposta.virou
      })
    } else {
      setEditingPropostaId(null)
      setFormData({
        nome_cliente: '',
        id_vendedor: vendedores[0]?.id ?? '',
        data: new Date().toISOString().split('T')[0],
        valor_proposta: '',
        origem: 'PAP',
        virou: false
      })
    }
    setMsg({ text: '', type: '' })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingPropostaId(null)
  }

  const openModalVendedor = () => {
    setEditingVendedorId(null)
    setFormVendedor({ nome: '', meta: '' })
    setMsg({ text: '', type: '' })
    setShowModalVendedor(true)
  }

  const closeModalVendedor = () => {
    setShowModalVendedor(false)
    setEditingVendedorId(null)
  }

  const handleSubmitVendedor = async (e) => {
    e.preventDefault()
    if (!formVendedor.nome?.trim()) {
      setMsg({ text: 'Informe o nome do vendedor.', type: 'error' })
      return
    }
    setSavingVendedor(true)
    setMsg({ text: '', type: '' })
    try {
      const meta = formVendedor.meta
        ? parseFloat(String(formVendedor.meta).replace(/\./g, '').replace(',', '.'))
        : 0

      if (editingVendedorId) {
        const { error } = await supabase
          .from('vendedores_proposta')
          .update({ nome: formVendedor.nome.trim(), meta })
          .eq('id', editingVendedorId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('vendedores_proposta').insert([
          { nome: formVendedor.nome.trim(), meta, id_empresa: userEmpresa?.id || null }
        ])
        if (error) throw error
      }
      closeModalVendedor()
      loadVendedores()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao cadastrar vendedor.', type: 'error' })
    } finally {
      setSavingVendedor(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const idV = formData.id_vendedor ? parseInt(formData.id_vendedor, 10) : null
    const valor = formData.valor_proposta
      ? parseFloat(String(formData.valor_proposta).replace(/\./g, '').replace(',', '.'))
      : 0
    if (!formData.nome_cliente?.trim()) {
      setMsg({ text: 'Informe o nome do cliente.', type: 'error' })
      return
    }
    if (!idV) {
      setMsg({ text: 'Selecione o vendedor.', type: 'error' })
      return
    }
    if (!formData.data) {
      setMsg({ text: 'Informe a data.', type: 'error' })
      return
    }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      if (editingPropostaId) {
        const { error } = await supabase
          .from('propostas_cadastro')
          .update({
            nome_cliente: formData.nome_cliente.trim(),
            id_vendedor: idV,
            data: formData.data,
            valor_proposta: valor,
            origem: formData.origem,
            virou: !!formData.virou
          })
          .eq('id', editingPropostaId)
        if (error) throw error
      } else {
        const { error } = await supabase.from('propostas_cadastro').insert([
          {
            nome_cliente: formData.nome_cliente.trim(),
            id_vendedor: idV,
            data: formData.data,
            valor_proposta: valor,
            origem: formData.origem,
            virou: !!formData.virou,
            id_empresa: userEmpresa?.id || null
          }
        ])
        if (error) throw error
      }
      closeModal()
      loadPropostas()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao salvar proposta.', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteProposta = async (proposta) => {
    if (!proposta?.id) return
    const ok = window.confirm(`Deseja realmente excluir a proposta do cliente "${proposta.nome_cliente}"?`)
    if (!ok) return
    try {
      setLoading(true)
      const { error } = await supabase.from('propostas_cadastro').delete().eq('id', proposta.id)
      if (error) throw error
      await loadPropostas()
    } catch (err) {
      console.error('Erro ao excluir proposta:', err)
      setMsg({ text: err.message || 'Erro ao excluir proposta.', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="propostas-cadastro">
      <div className="propostas-cadastro-header">
        <button type="button" className="propostas-cadastro-btn-back" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="propostas-cadastro-title">Propostas (Cadastro)</h1>
        <button
          type="button"
          className="propostas-cadastro-btn-toggle-filtros"
          onClick={() => setShowFiltros((prev) => !prev)}
          aria-expanded={showFiltros}
        >
          {showFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
        </button>
      </div>

      {showFiltros && (
        <div className="propostas-cadastro-filtros">
          <label className="propostas-cadastro-label">
            Mês
            <input
              type="month"
              className="propostas-cadastro-input"
              value={mesAno}
              onChange={(e) => setMesAno(e.target.value)}
            />
          </label>
          <label className="propostas-cadastro-label">
            Vendedor
            <select
              className="propostas-cadastro-select"
              value={filtroVendedor}
              onChange={(e) => setFiltroVendedor(e.target.value)}
            >
              <option value="">Todos</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>{v.nome}</option>
              ))}
            </select>
          </label>
          <button type="button" className="propostas-cadastro-btn-cadastrar" onClick={openModalProposta}>
            Cadastrar proposta
          </button>
          <button type="button" className="propostas-cadastro-btn-vendedor" onClick={openModalVendedor}>
            Cadastrar vendedor
          </button>
        </div>
      )}

      {loading && <div className="propostas-cadastro-loading">Carregando...</div>}

      {!loading && (
        <>
          <div className="propostas-cadastro-card-total">
            <span className="propostas-cadastro-card-total-label">Valor total</span>
            <div className="propostas-cadastro-card-total-right">
              <span className="propostas-cadastro-card-total-value">R$ {formatarMoeda(totalValor)}</span>
              <button
                type="button"
                className="propostas-cadastro-btn-atualizar"
                onClick={() => {
                  loadPropostas()
                  loadEventosCalendario()
                  loadEventosDiaHoje()
                }}
                title="Atualizar informações"
                aria-label="Atualizar informações"
              >
                Atualizar
              </button>
            </div>
          </div>
          <div className="propostas-cadastro-card-total propostas-cadastro-card-valor-virado">
            <span className="propostas-cadastro-card-total-label">Faturado</span>
            <span className="propostas-cadastro-card-total-value">R$ {formatarMoeda(totalValorVirado)}</span>
          </div>
          <div className="propostas-cadastro-cards-qtd">
            <div className="propostas-cadastro-card-qtd">
              <span className="propostas-cadastro-card-qtd-label">PAP</span>
              <span className="propostas-cadastro-card-qtd-value">{qtdPAP}</span>
            </div>
            <div className="propostas-cadastro-card-qtd">
              <span className="propostas-cadastro-card-qtd-label">LISTA BANCO</span>
              <span className="propostas-cadastro-card-qtd-value">{qtdListaBanco}</span>
            </div>
            <div className="propostas-cadastro-card-qtd">
              <span className="propostas-cadastro-card-qtd-label">INDICAÇÃO</span>
              <span className="propostas-cadastro-card-qtd-value">{qtdIndicacao}</span>
            </div>
            <div className="propostas-cadastro-card-qtd">
              <span className="propostas-cadastro-card-qtd-label">LIGAÇÃO</span>
              <span className="propostas-cadastro-card-qtd-value">{qtdLigacao}</span>
            </div>
            <div className="propostas-cadastro-card-qtd">
              <span className="propostas-cadastro-card-qtd-label">AGENDAMENTO</span>
              <span className="propostas-cadastro-card-qtd-value">{qtdAgendamento}</span>
            </div>
          </div>
          <section className="propostas-cadastro-charts">
            <div className="propostas-cadastro-card">
              <h3>Valor por vendedor</h3>
              <div className="propostas-cadastro-bars">
                {dadosGraficoVendedor.length === 0 ? (
                  <p className="propostas-cadastro-empty">Nenhum dado no período.</p>
                ) : (
                  dadosGraficoVendedor.map((d, i) => (
                    <div key={d.id} className="propostas-cadastro-bar-row">
                      <div className="propostas-cadastro-bar-label">{d.nome}</div>
                      <div className="propostas-cadastro-bar-track">
                        <div
                          className="propostas-cadastro-bar-fill"
                          style={{
                            width: `${(d.valor / maxValorVendedor) * 100}%`,
                            backgroundColor: coresBarras[i % coresBarras.length]
                          }}
                        />
                      </div>
                      <span className="propostas-cadastro-bar-value">R$ {formatarMoeda(d.valor)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="propostas-cadastro-card">
              <h3>Valor por origem</h3>
              <div className="propostas-cadastro-bars">
                {dadosGraficoOrigem.length === 0 ? (
                  <p className="propostas-cadastro-empty">Nenhum dado no período.</p>
                ) : (
                  dadosGraficoOrigem.map((d, i) => (
                    <div key={d.origem} className="propostas-cadastro-bar-row">
                      <div className="propostas-cadastro-bar-label">{d.origem}</div>
                      <div className="propostas-cadastro-bar-track">
                        <div
                          className="propostas-cadastro-bar-fill"
                          style={{
                            width: `${(d.valor / maxValorOrigem) * 100}%`,
                            backgroundColor: coresBarras[i % coresBarras.length]
                          }}
                        />
                      </div>
                      <span className="propostas-cadastro-bar-value">R$ {formatarMoeda(d.valor)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="propostas-cadastro-card">
              <h3>Agendamentos (eventos calendário)</h3>
              <div className="propostas-cadastro-bars">
                {loadingEventos ? (
                  <p className="propostas-cadastro-empty">Carregando...</p>
                ) : dadosGraficoAgendamentos.length === 0 ? (
                  <p className="propostas-cadastro-empty">Nenhum agendamento no período.</p>
                ) : (
                  dadosGraficoAgendamentos.map((d, i) => (
                    <div key={d.id} className="propostas-cadastro-bar-row">
                      <div className="propostas-cadastro-bar-label">{d.nome}</div>
                      <div className="propostas-cadastro-bar-track">
                        <div
                          className="propostas-cadastro-bar-fill"
                          style={{
                            width: `${(d.count / maxAgendamentos) * 100}%`,
                            backgroundColor: coresBarras[i % coresBarras.length]
                          }}
                        />
                      </div>
                      <span className="propostas-cadastro-bar-value">{d.count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="propostas-cadastro-card propostas-cadastro-card-agendamentos-dia">
              <h3>Agendamentos do Dia</h3>
              {loadingEventosDia ? (
                <p className="propostas-cadastro-empty">Carregando...</p>
              ) : eventosDiaHoje.length === 0 ? (
                <p className="propostas-cadastro-empty">Nenhum agendamento hoje.</p>
              ) : (
                <ul className="propostas-cadastro-lista-dia">
                  {eventosDiaHoje.map((ev) => (
                    <li key={ev.id} className="propostas-cadastro-item-dia">
                      <span className="propostas-cadastro-item-dia-titulo">
                        {ev.nome_evento || 'Evento'} – {formatarDataHora(ev.data_hora)}
                      </span>
                      <span className="propostas-cadastro-item-dia-responsavel">{ev.nome_responsavel}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="propostas-cadastro-tabela-wrap">
            <h3 className="propostas-cadastro-tabela-title">Clientes cadastrados (propostas)</h3>
            <div className="propostas-cadastro-tabela-container">
              <table className="propostas-cadastro-tabela">
                <thead>
                  <tr>
                    <th>Nome do cliente</th>
                    <th>Vendedor</th>
                    <th>Data</th>
                    <th>Valor proposta</th>
                    <th>Origem</th>
                    <th>Virou</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {propostas.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="propostas-cadastro-tabela-empty">
                        Nenhum registro no período.
                      </td>
                    </tr>
                  ) : (
                    propostas.map((p) => (
                      <tr key={p.id}>
                        <td>{p.nome_cliente || '–'}</td>
                        <td>{p.vendedores_proposta?.nome ?? '–'}</td>
                        <td>{formatarData(p.data)}</td>
                        <td>R$ {formatarMoeda(p.valor_proposta)}</td>
                        <td>{p.origem || '–'}</td>
                        <td>{p.virou ? 'Sim' : 'Não'}</td>
                        <td>
                          <button
                            type="button"
                            className="propostas-cadastro-acao-btn"
                            onClick={() => openModalProposta(p)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="propostas-cadastro-acao-btn propostas-cadastro-acao-btn-danger"
                            onClick={() => handleDeleteProposta(p)}
                          >
                            Excluir
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {showModal && (
        <div className="propostas-cadastro-modal-overlay" onClick={closeModal}>
          <div className="propostas-cadastro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="propostas-cadastro-modal-header">
              <h2>{editingPropostaId ? 'Editar proposta' : 'Nova proposta'}</h2>
              <button type="button" className="propostas-cadastro-modal-close" onClick={closeModal} aria-label="Fechar">×</button>
            </div>
            <form onSubmit={handleSubmit} className="propostas-cadastro-form">
              {msg.text && (
                <div className={`propostas-cadastro-msg propostas-cadastro-msg-${msg.type}`}>{msg.text}</div>
              )}
              <label className="propostas-cadastro-form-label">
                Nome do cliente *
                <input
                  type="text"
                  className="propostas-cadastro-form-input"
                  value={formData.nome_cliente}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nome_cliente: e.target.value }))}
                  placeholder="Nome do cliente"
                />
              </label>
              <label className="propostas-cadastro-form-label">
                Vendedor *
                <select
                  className="propostas-cadastro-form-select"
                  value={formData.id_vendedor}
                  onChange={(e) => setFormData((prev) => ({ ...prev, id_vendedor: e.target.value }))}
                >
                  <option value="">Selecione</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>{v.nome}</option>
                  ))}
                </select>
              </label>
              <label className="propostas-cadastro-form-label">
                Data *
                <input
                  type="date"
                  className="propostas-cadastro-form-input"
                  value={formData.data}
                  onChange={(e) => setFormData((prev) => ({ ...prev, data: e.target.value }))}
                />
              </label>
              <label className="propostas-cadastro-form-label">
                Valor da proposta (R$)
                <input
                  type="text"
                  className="propostas-cadastro-form-input"
                  value={formData.valor_proposta}
                  onChange={(e) => setFormData((prev) => ({ ...prev, valor_proposta: formatarValorInput(e.target.value) }))}
                  placeholder="0,00"
                />
              </label>
              <label className="propostas-cadastro-form-label">
                Origem
                <select
                  className="propostas-cadastro-form-select"
                  value={formData.origem}
                  onChange={(e) => setFormData((prev) => ({ ...prev, origem: e.target.value }))}
                >
                  {ORIGENS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </label>
              <label className="propostas-cadastro-form-label propostas-cadastro-form-check">
                <input
                  type="checkbox"
                  checked={formData.virou}
                  onChange={(e) => setFormData((prev) => ({ ...prev, virou: e.target.checked }))}
                />
                <span>Virou (sim/não)</span>
              </label>
              <div className="propostas-cadastro-form-actions">
                <button type="button" className="propostas-cadastro-btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="propostas-cadastro-btn-save" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModalVendedor && (
        <div className="propostas-cadastro-modal-overlay" onClick={closeModalVendedor}>
          <div className="propostas-cadastro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="propostas-cadastro-modal-header">
              <h2>{editingVendedorId ? 'Editar vendedor' : 'Novo vendedor'}</h2>
              <button type="button" className="propostas-cadastro-modal-close" onClick={closeModalVendedor} aria-label="Fechar">×</button>
            </div>
            <form onSubmit={handleSubmitVendedor} className="propostas-cadastro-form">
              {msg.text && showModalVendedor && (
                <div className={`propostas-cadastro-msg propostas-cadastro-msg-${msg.type}`}>{msg.text}</div>
              )}
              <label className="propostas-cadastro-form-label">
                Selecionar vendedor para editar
                <select
                  className="propostas-cadastro-form-select"
                  value={editingVendedorId || ''}
                  onChange={(e) => {
                    const id = e.target.value || null
                    setEditingVendedorId(id)
                    if (id) {
                      const vend = vendedores.find((v) => String(v.id) === String(id))
                      if (vend) {
                        setFormVendedor({
                          nome: vend.nome || '',
                          meta: vend.meta != null ? formatarValorInput(vend.meta) : ''
                        })
                      }
                    } else {
                      setFormVendedor({ nome: '', meta: '' })
                    }
                  }}
                >
                  <option value="">Novo vendedor</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label className="propostas-cadastro-form-label">
                Nome do vendedor *
                <input
                  type="text"
                  className="propostas-cadastro-form-input"
                  value={formVendedor.nome}
                  onChange={(e) => setFormVendedor((prev) => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome"
                />
              </label>
              <label className="propostas-cadastro-form-label">
                Meta (R$)
                <input
                  type="text"
                  className="propostas-cadastro-form-input"
                  value={formVendedor.meta}
                  onChange={(e) =>
                    setFormVendedor((prev) => ({
                      ...prev,
                      meta: formatarValorInput(e.target.value)
                    }))
                  }
                  placeholder="0,00"
                />
              </label>
              <div className="propostas-cadastro-form-actions">
                <button type="button" className="propostas-cadastro-btn-cancel" onClick={closeModalVendedor}>
                  Cancelar
                </button>
                <button type="submit" className="propostas-cadastro-btn-save" disabled={savingVendedor}>
                  {savingVendedor ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default PropostasCadastro
